import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CONSENT_OPEN_PREFERENCES_EVENT, defaultConsentPreferences } from "./config";
import { applyConsentServices, clearOptionalCookies } from "./services";
import { createConsentDecision, loadStoredConsentDecision, saveStoredConsentDecision } from "./storage";
import { ConsentCategory, ConsentPreferences, StoredConsentDecision } from "./types";

interface ConsentContextValue {
  decision: StoredConsentDecision | null;
  preferences: ConsentPreferences;
  hasResolvedConsent: boolean;
  settingsOpen: boolean;
  bannerVisible: boolean;
  hasConsent: (category: ConsentCategory) => boolean;
  openSettings: () => void;
  closeSettings: () => void;
  setCategory: (category: ConsentCategory, value: boolean) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  saveCurrentSelection: () => void;
}

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

export const ConsentProvider = ({ children }: { children: React.ReactNode }) => {
  const [decision, setDecision] = useState<StoredConsentDecision | null>(null);
  const [preferences, setPreferences] = useState<ConsentPreferences>(defaultConsentPreferences);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(false);

  useEffect(() => {
    const stored = loadStoredConsentDecision();
    if (stored) {
      setDecision(stored);
      setPreferences(stored.preferences);
      setBannerVisible(false);
      applyConsentServices(stored.preferences);
      clearOptionalCookies(stored.preferences);
    } else {
      setDecision(null);
      setPreferences(defaultConsentPreferences);
      setBannerVisible(true);
    }
  }, []);

  useEffect(() => {
    const openHandler = () => setSettingsOpen(true);
    window.addEventListener(CONSENT_OPEN_PREFERENCES_EVENT, openHandler);
    return () => window.removeEventListener(CONSENT_OPEN_PREFERENCES_EVENT, openHandler);
  }, []);

  const persistDecision = useCallback((nextPreferences: ConsentPreferences) => {
    const nextDecision = createConsentDecision(nextPreferences);
    setDecision(nextDecision);
    setPreferences(nextDecision.preferences);
    saveStoredConsentDecision(nextDecision);
    applyConsentServices(nextDecision.preferences);
    clearOptionalCookies(nextDecision.preferences);
    setBannerVisible(false);
    setSettingsOpen(false);
    window.dispatchEvent(new CustomEvent("eckenmoorhexen:consent-changed", { detail: nextDecision }));
  }, []);

  const setCategory = useCallback((category: ConsentCategory, value: boolean) => {
    setPreferences((current) => ({
      ...current,
      [category]: category === "essential" ? true : value,
    }));
  }, []);

  const acceptAll = useCallback(() => {
    persistDecision({
      essential: true,
      statistics: true,
      marketing: true,
      external_media: true,
      comfort: true,
    });
  }, [persistDecision]);

  const rejectAll = useCallback(() => {
    persistDecision({
      essential: true,
      statistics: false,
      marketing: false,
      external_media: false,
      comfort: false,
    });
  }, [persistDecision]);

  const saveCurrentSelection = useCallback(() => {
    persistDecision(preferences);
  }, [persistDecision, preferences]);

  const value = useMemo<ConsentContextValue>(() => ({
    decision,
    preferences,
    hasResolvedConsent: decision !== null,
    settingsOpen,
    bannerVisible,
    hasConsent: (category) => category === "essential" || preferences[category],
    openSettings: () => setSettingsOpen(true),
    closeSettings: () => setSettingsOpen(false),
    setCategory,
    acceptAll,
    rejectAll,
    saveCurrentSelection,
  }), [acceptAll, bannerVisible, decision, preferences, rejectAll, saveCurrentSelection, setCategory, settingsOpen]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error("useConsent must be used within ConsentProvider");
  }

  return context;
};
